/**
 * Cscrollable Component
 * Scrollable container with dynamic fade effects at edges using CSS mask-image
 * Automatically adapts to parent width/height with smooth fade indicators
 * Based on: https://pqina.nl/blog/fade-out-overflow-using-css-mask-image/
 */

'use client';

import type { ReactElement } from 'react';
import {
  CROSS_AXIS_OVERFLOW,
  CSCROLLABLE_DEFAULT_CROSS_AXIS_BLEED_PX,
  CSCROLLABLE_ROOT_CLASS_NAME,
  SCROLL_DIRECTION,
} from './Cscrollable.constants';
import { CscrollableRoot } from './CscrollableRoot';
import type { CscrollableProps, CrossAxisOverflow, ScrollDirection } from './Cscrollable.types';
import { useCscrollableBehavior } from './useCscrollableBehavior';
import { useCscrollableStyle } from './useCscrollableStyle';

export { CROSS_AXIS_OVERFLOW, SCROLL_DIRECTION };
export type { CscrollableProps, CrossAxisOverflow, ScrollDirection };

export function Cscrollable({
  children,
  dataElScope,
  direction = SCROLL_DIRECTION.Vertical,
  fadeSize = 40,
  horizontalFadeSize = 0,
  verticalFadeTopSize = 70,
  verticalFadeBottomSize = 100,
  verticalFadeMiddleOpacity = 0.77,
  firstItemSpacing = 3,
  scrollRef: externalScrollRef,
  onScroll: onScrollCallback,
  crossAxisOverflow = CROSS_AXIS_OVERFLOW.Hidden,
  crossAxisBleed = CSCROLLABLE_DEFAULT_CROSS_AXIS_BLEED_PX,
  className,
  style,
  debugName,
  debugContext,
  enableLayoutScroll = false,
}: CscrollableProps): ReactElement {
  const scrollableClassName = className
    ? `${CSCROLLABLE_ROOT_CLASS_NAME} ${className}`
    : CSCROLLABLE_ROOT_CLASS_NAME;
  const scrollRef = useCscrollableBehavior({
    children,
    direction,
    fadeSize,
    horizontalFadeSize,
    verticalFadeTopSize,
    verticalFadeBottomSize,
    verticalFadeMiddleOpacity,
    firstItemSpacing,
    externalScrollRef,
    onScrollCallback,
    debugName,
    debugContext,
  });
  const scrollableStyle = useCscrollableStyle({
    direction,
    crossAxisOverflow,
    crossAxisBleed,
    fadeSize,
    firstItemSpacing,
    style,
  });

  return (
    <CscrollableRoot
      dataElScope={dataElScope}
      enableLayoutScroll={enableLayoutScroll}
      scrollRef={scrollRef}
      className={scrollableClassName}
      style={scrollableStyle}
    >
      {children}
    </CscrollableRoot>
  );
}
